import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Switch } from '../../../components/ui/switch';
import { Save, Loader2, Percent, FileText, Building } from 'lucide-react';

export function TaxSettings({ data, setData, isSaving, onSave }) {
  return (
    <Card data-testid="tax-settings-card">
      <CardHeader>
        <div className="flex items-center gap-3">
          <div className="p-2 bg-green-100 rounded-lg">
            <Percent className="w-5 h-5 text-green-600" />
          </div>
          <div>
            <CardTitle>Tax Settings</CardTitle>
            <CardDescription>Configure tax rates for booking transactions</CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Enable Tax */}
        <div className="flex items-center justify-between p-4 border rounded-lg">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-50 rounded-lg">
              <FileText className="w-4 h-4 text-green-600" />
            </div>
            <div>
              <Label className="text-base">Enable Tax</Label>
              <p className="text-sm text-gray-500">
                Apply tax to all booking transactions
              </p>
            </div>
          </div>
          <Switch
            checked={data.tax_enabled}
            onCheckedChange={(checked) => setData({ ...data, tax_enabled: checked })}
            data-testid="tax-enabled-switch"
          />
        </div>

        {data.tax_enabled && (
          <>
            {/* Tax Rate */}
            <div className="space-y-2">
              <Label htmlFor="tax_rate">Tax Rate (%)</Label>
              <div className="flex items-center gap-2">
                <Input
                  id="tax_rate"
                  type="number"
                  min="0"
                  max="100"
                  step="0.1"
                  value={(data.tax_rate * 100).toFixed(1)}
                  onChange={(e) => setData({ ...data, tax_rate: parseFloat(e.target.value) / 100 || 0 })}
                  className="w-32"
                  data-testid="tax-rate-input"
                />
                <span className="text-gray-500">%</span>
              </div>
              <p className="text-sm text-gray-500">
                Default: 12% (Philippine VAT rate)
              </p>
            </div>

            {/* Tax Name */}
            <div className="space-y-2">
              <Label htmlFor="tax_name">Tax Name</Label>
              <Input
                id="tax_name"
                type="text"
                value={data.tax_name}
                onChange={(e) => setData({ ...data, tax_name: e.target.value })}
                placeholder="e.g., VAT, GST, Sales Tax"
                data-testid="tax-name-input"
              />
              <p className="text-sm text-gray-500">
                This name will appear on invoices and receipts
              </p>
            </div>

            {/* Tax Registration Number */}
            <div className="space-y-2">
              <Label htmlFor="tax_registration_number">Tax Registration Number (Optional)</Label>
              <Input
                id="tax_registration_number"
                type="text"
                value={data.tax_registration_number}
                onChange={(e) => setData({ ...data, tax_registration_number: e.target.value })}
                placeholder="e.g., TIN or VAT Registration Number"
                data-testid="tax-registration-input"
              />
              <p className="text-sm text-gray-500">
                Your business tax registration number for compliance
              </p>
            </div>

            {/* Display on Invoice */}
            <div className="flex items-center justify-between p-4 border rounded-lg">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-50 rounded-lg">
                  <FileText className="w-4 h-4 text-blue-600" />
                </div>
                <div>
                  <Label className="text-base">Display on Invoice/Receipt</Label>
                  <p className="text-sm text-gray-500">
                    Show tax breakdown on customer invoices and receipts
                  </p>
                </div>
              </div>
              <Switch
                checked={data.tax_display_on_invoice}
                onCheckedChange={(checked) => setData({ ...data, tax_display_on_invoice: checked })}
                data-testid="tax-display-invoice-switch"
              />
            </div>

            {/* Tax Included in Price */}
            <div className="flex items-center justify-between p-4 border rounded-lg">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-amber-50 rounded-lg">
                  <Building className="w-4 h-4 text-amber-600" />
                </div>
                <div>
                  <Label className="text-base">Tax Included in Price</Label>
                  <p className="text-sm text-gray-500">
                    {data.tax_included_in_price 
                      ? "Service prices already include tax (tax-inclusive pricing)" 
                      : "Tax is added on top of service prices (tax-exclusive pricing)"}
                  </p>
                </div>
              </div>
              <Switch
                checked={data.tax_included_in_price}
                onCheckedChange={(checked) => setData({ ...data, tax_included_in_price: checked })}
                data-testid="tax-included-switch"
              />
            </div>

            {/* Tax Preview */}
            <div className="p-4 bg-gray-50 rounded-lg">
              <h4 className="font-medium text-gray-900 mb-2">Tax Preview</h4>
              <p className="text-sm text-gray-600">
                For a Php 1,000 service:
              </p>
              <div className="mt-2 space-y-1">
                <div className="flex justify-between text-sm">
                  <span>Subtotal:</span>
                  <span>Php 1,000.00</span>
                </div>
                <div className="flex justify-between text-sm text-green-600">
                  <span>{data.tax_name || 'Tax'} ({(data.tax_rate * 100).toFixed(1)}%):</span>
                  <span>Php {(1000 * data.tax_rate).toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm font-medium pt-2 border-t">
                  <span>Total:</span>
                  <span>Php {(1000 + 1000 * data.tax_rate).toFixed(2)}</span>
                </div>
              </div>
            </div>
          </>
        )}

        {/* Save Button */}
        <Button 
          onClick={onSave} 
          disabled={isSaving}
          className="w-full"
          data-testid="save-tax-settings-btn"
        >
          {isSaving ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Save className="w-4 h-4 mr-2" />
              Save Tax Settings
            </>
          )}
        </Button>
      </CardContent>
    </Card>
  );
}
